import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaPaperclip, FaTimes, FaChevronLeft, FaTicketAlt } from 'react-icons/fa';
import AuthContext from '../../context/AuthContext';
import {
    listMyTickets,
    getMyTicket,
    addUserMessage,
} from '../../services/supportTicketsApi';

const STATUS_COLORS = {
    open: { bg: '#FFF4E5', color: '#B26A00' },
    in_progress: { bg: '#E3F2FD', color: '#1565C0' },
    resolved: { bg: '#E6F4EA', color: '#2E7D32' },
    closed: { bg: '#ECEFF1', color: '#546E7A' },
};

const formatTime = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString('en-IN', {
            timeZone: 'Asia/Kolkata',
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
            hour12: false,
        }) + ' IST';
    } catch { return iso; }
};

const StatusPill = ({ status }) => {
    const s = STATUS_COLORS[status] || STATUS_COLORS.open;
    const label = (status || 'open').replace('_', ' ');
    return (
        <span style={{ background: s.bg, color: s.color, padding: '3px 10px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600, textTransform: 'capitalize' }}>
            {label}
        </span>
    );
};

const TicketThread = ({ ticket, onBack, onTicketUpdate }) => {
    const authCtx = useContext(AuthContext);
    const [reply, setReply] = useState('');
    const [files, setFiles] = useState([]);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');

    const onFileChange = (e) => {
        const picked = Array.from(e.target.files || []);
        setFiles([...files, ...picked].slice(0, 5));
        e.target.value = '';
    };
    const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

    const sendReply = async (e) => {
        e.preventDefault();
        setError('');
        if (!reply.trim()) { setError('Message cannot be empty.'); return; }
        setSending(true);
        try {
            const res = await addUserMessage(authCtx.token, ticket.ticket_id, { message: reply.trim(), attachments: files });
            onTicketUpdate(res.ticket);
            setReply(''); setFiles([]);
        } catch (err) {
            setError(err.message || 'Failed to send.');
        } finally {
            setSending(false);
        }
    };

    return (
        <div style={{ background: 'white', padding: 20, borderRadius: 16, border: '1px solid #E8F0FE', boxShadow: '0 0 20px rgba(120,179,243,0.08)' }}>
            <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2766AA', fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 16, padding: 0 }}>
                <FaChevronLeft /> Back to tickets
            </button>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div>
                    <h3 style={{ margin: 0, color: '#2c3e50', fontSize: '1.2rem' }}>{ticket.subject}</h3>
                    <div style={{ fontSize: '0.8rem', color: '#737373', marginTop: 4 }}>
                        {ticket.category_label} · Created {formatTime(ticket.created_at)}
                    </div>
                </div>
                <StatusPill status={ticket.status} />
            </div>

            <div style={{ marginTop: 16, maxHeight: 460, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, padding: '8px 4px' }}>
                {(ticket.messages || []).map((m) => (
                    <div key={m.message_id} style={{ display: 'flex', flexDirection: 'column', alignItems: m.sender === 'admin' ? 'flex-start' : 'flex-end' }}>
                        <div style={{ background: m.sender === 'admin' ? '#F1F5F9' : '#E3F2FD', color: '#2c3e50', padding: '10px 14px', borderRadius: 10, maxWidth: '80%', fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>
                            <div style={{ fontSize: '0.7rem', color: '#737373', marginBottom: 4 }}>
                                <strong>{m.sender === 'admin' ? (m.sender_name || 'Support') : 'You'}</strong> · {formatTime(m.created_at)}
                            </div>
                            <div>{m.text}</div>
                            {(m.attachments || []).length > 0 && (
                                <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                    {m.attachments.map((a, idx) => (
                                        <a key={idx} href={a.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.78rem', color: '#2766AA' }}>
                                            <FaPaperclip /> {a.filename}
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {ticket.status !== 'closed' && (
                <form onSubmit={sendReply} style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <textarea
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        rows={3}
                        placeholder="Type your reply…"
                        style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #D0E7FF', outline: 'none', fontSize: '0.9rem', resize: 'vertical' }}
                    />
                    {files.length > 0 && (
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {files.map((f, i) => (
                                <li key={i} style={{ display: 'flex', justifyContent: 'space-between', background: '#F8FBFF', padding: '4px 8px', borderRadius: 4, fontSize: '0.8rem' }}>
                                    <span>{f.name}</span>
                                    <button type="button" onClick={() => removeFile(i)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FaTimes /></button>
                                </li>
                            ))}
                        </ul>
                    )}
                    {error && <div style={{ fontSize: '0.85rem', color: '#d9534f' }}>{error}</div>}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 6, border: '1px dashed #78B3F3', cursor: 'pointer', color: '#2766AA', fontSize: '0.8rem' }}>
                            <FaPaperclip /> Attach
                            <input type="file" multiple onChange={onFileChange} style={{ display: 'none' }} />
                        </label>
                        <button type="submit" disabled={sending} style={{ padding: '8px 18px', background: '#2766AA', color: 'white', border: 'none', borderRadius: 6, fontWeight: 600, cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? 0.6 : 1 }}>
                            {sending ? 'Sending…' : 'Send Reply'}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
};

const SupportTicketsPage = () => {
    const authCtx = useContext(AuthContext);
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState(null);

    const refreshList = useCallback(async () => {
        if (!authCtx.token) return;
        setLoading(true);
        setError('');
        try {
            const data = await listMyTickets(authCtx.token);
            setTickets(data.tickets || []);
        } catch (err) {
            setError(err.message || 'Failed to load tickets.');
        } finally {
            setLoading(false);
        }
    }, [authCtx.token]);

    useEffect(() => { refreshList(); }, [refreshList]);

    const openTicket = async (ticketId) => {
        try {
            const data = await getMyTicket(authCtx.token, ticketId);
            setSelected(data.ticket);
        } catch (err) {
            setError(err.message || 'Failed to load ticket.');
        }
    };

    const onTicketUpdate = (updated) => {
        setSelected(updated);
        setTickets((prev) => prev.map(t => t.ticket_id === updated.ticket_id ? updated : t));
    };

    return (
        <div style={{ maxWidth: 900, margin: '24px auto', padding: '0 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h1 style={{ margin: 0, color: '#2c3e50', fontSize: '1.6rem' }}>Manage Support Tickets</h1>
                <Link to="/dashboard/help" style={{ padding: '8px 16px', background: '#2766AA', color: 'white', borderRadius: 8, fontSize: '0.9rem', fontWeight: 600, textDecoration: 'none' }}>
                    Raise a New Ticket
                </Link>
            </div>

            {selected ? (
                <TicketThread ticket={selected} onBack={() => setSelected(null)} onTicketUpdate={onTicketUpdate} />
            ) : loading ? (
                <div style={{ color: '#737373' }}>Loading…</div>
            ) : error ? (
                <div style={{ color: '#d9534f' }}>{error}</div>
            ) : tickets.length === 0 ? (
                <div style={{ background: 'white', padding: 40, borderRadius: 16, border: '1px solid #E8F0FE', textAlign: 'center', color: '#737373' }}>
                    <FaTicketAlt style={{ fontSize: 36, color: '#B6C9E8', marginBottom: 12 }} />
                    <p style={{ margin: 0 }}>You haven't raised any tickets yet.</p>
                    <p style={{ margin: '8px 0 0', fontSize: '0.9rem' }}>
                        Head to <Link to="/dashboard/help" style={{ color: '#2766AA', fontWeight: 600 }}>Help → Raise a Ticket</Link> to get started.
                    </p>
                </div>
            ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {tickets.map((t) => (
                        <li
                            key={t.ticket_id}
                            onClick={() => openTicket(t.ticket_id)}
                            style={{ padding: '14px 16px', border: '1px solid #E8F0FE', borderRadius: 12, cursor: 'pointer', background: '#fff', transition: 'background 0.15s', boxShadow: '0 0 20px rgba(120,179,243,0.05)' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#F8FBFF'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontWeight: 600, color: '#2c3e50', fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {t.subject}
                                </span>
                                <StatusPill status={t.status} />
                            </div>
                            <div style={{ fontSize: '0.78rem', color: '#737373', marginTop: 4 }}>
                                {t.category_label} · Updated {formatTime(t.updated_at)} · {(t.messages || []).length} message{(t.messages || []).length === 1 ? '' : 's'}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default SupportTicketsPage;
